/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { updateUser } from "../../../api/User";
import { UserAtom } from "../../../atoms/UserAtom";
import { IUserIRoute } from "../../../generated/Api";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import StyledTextArea from "../../StyledTextArea";
import { useModal } from "../Modal";
import { useEffect } from "react";

const ModalBody = styled.div<{ testDriveMode?: boolean }>`
  height: ${(props) => (props.testDriveMode ? "100%" : "fit-content")};
  width: ${(props) => (props.testDriveMode ? "480px" : "480px")};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;

  ${(props) =>
    props.testDriveMode &&
    css`
      border: none;

      > div {
        border: none;
        padding-top: 0px;
      }
    `}
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;

  .buttons-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    grid-gap: 16px;
  }
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

interface Props {
  testDriveMode?: boolean;
  testDriveCanSpamCreated?: () => Promise<void>;
}

export default function UpdateCanSpam(props: Props): JSX.Element | null {
  const [disable, setDisable] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [user, setUser] = useRecoilState(UserAtom);

  // CAN-SPAM
  const [canSpamAddress, setCanSpamAddress] = useState(user?.address || "");

  const companyRef = useRef<HTMLInputElement>(null);
  const companyPhoneRef = useRef<HTMLInputElement>(null);

  const modal = useModal();

  function checkInput(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value.trim().length < 1) setDisable(true);
    else if (disable && event.target.value.trim().length >= 1)
      setDisable(false);
  }

  async function sendUpdateUser() {
    if (!user) return false;

    if (!companyRef.current?.value || companyRef.current?.value.trim() === "") {
      return toast("Your name can't be empty!", {
        type: "error",
      });
    }

    if (!canSpamAddress || canSpamAddress.trim() === "") {
      return toast("CAN-SPAM Address can't be empty!", {
        type: "error",
      });
    }

    setLoading(true);

    const userObject: IUserIRoute = {
      ...user,
      company: companyRef.current?.value || "",
      address: canSpamAddress || "",
      companyPhone: companyPhoneRef.current?.value || "",
    };

    delete (userObject as any)._id;
    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        company: companyRef.current?.value || "",
        address: canSpamAddress || "",
        companyPhone: companyPhoneRef.current?.value || "",
      });
      toast("CAN-SPAM updated", {
        type: "success",
      });
      modal.popModal();
    }

    if (props.testDriveCanSpamCreated) await props.testDriveCanSpamCreated();

    setLoading(false);
    return false;
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        sendUpdateUser();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [user?.company, canSpamAddress, user?.companyPhone]);

  if (!user) return null;

  return (
    <ModalBody testDriveMode={props.testDriveMode}>
      <Wrapper>
        <InputLabel>
          <Title>Company or Individual Name</Title>
          <StyledInput
            placeholder="Enter your name"
            ref={companyRef}
            onChange={checkInput}
            defaultValue={user?.company}
          />
        </InputLabel>

        <InputLabel style={{ marginTop: 20 }}>
          <Title>CAN-SPAM Address</Title>
          <StyledTextArea
            value={canSpamAddress}
            onChange={(evt) => setCanSpamAddress(evt.target.value)}
            placeholder="Enter your CAN-SPAM Address"
            css={css`
              height: 70px;
            `}
          />
        </InputLabel>

        <InputLabel style={{ marginTop: 20, width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Title>CAN-SPAM Phone</Title>
            <Title style={{ opacity: 0.3, fontWeight: "normal", fontSize: 14 }}>
              optional
            </Title>
          </div>
          <StyledInput
            placeholder="Enter your CAN-SPAM Phone"
            ref={companyPhoneRef}
            defaultValue={user?.companyPhone}
          />
        </InputLabel>
      </Wrapper>

      <Buttons>
        {props.testDriveMode ? (
          <StyledButton
            disable={disable}
            isLoading={isLoading}
            onClick={() => sendUpdateUser()}
            css={css`
              width: 100%;
            `}
          >
            Set my CAN-SPAM
          </StyledButton>
        ) : (
          <div className="buttons-wrapper">
            <StyledButton
              inverted
              onClick={() => modal.popModal()}
              css={css`
                width: 80px;
              `}
            >
              Cancel
            </StyledButton>
            <StyledButton
              disable={disable}
              isLoading={isLoading}
              onClick={() => sendUpdateUser()}
              css={css`
                width: 80px;
              `}
            >
              Save
            </StyledButton>
          </div>
        )}
      </Buttons>
    </ModalBody>
  );
}
